/**
 * Payout Service — host bank account onboarding + payout history.
 *
 * Wraps payout_accounts + payouts tables and the payout account
 * edge function. Host flow:
 *
 *   1. createPayoutAccount({ bank info }) — calls edge fn which creates
 *      a provider account for the host's bank details.
 *   2. listAccounts() — show existing accounts on the payout screen
 *   3. setDefaultAccount(id) — pick which account receives future payouts
 *   4. listPayouts() — history table
 *
 * Class-based singleton per CLAUDE.md architecture.
 */
import { supabase } from '../supabase'
import type { Cols } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import type { Payout, PayoutAccount, PayoutAccountInput } from '../types/payout.types'

const sb = supabase

/** Exact display-only shape returned by the payout account RPCs. */
type PayoutAccountRow = {
  id: string
  bank_name: string | null
  account_number_masked: string | null
  account_holder_name: string | null
  is_default: boolean
  is_verified: boolean
}

/** Shape returned by `PAYOUT_SELECT`. */
type PayoutRow = Cols<
  'payouts',
  | 'id'
  | 'profile_id'
  | 'organization_id'
  | 'payout_account_id'
  | 'amount_cents'
  | 'currency'
  | 'provider'
  | 'provider_payout_id'
  | 'status'
  | 'status_reason'
  | 'initiated_at'
  | 'completed_at'
  | 'failed_at'
>

// PostgREST rejects select strings containing literal newlines, so keep this
// single-line. Both payout readers share it; `PayoutRow` above is its shape.
const PAYOUT_SELECT =
  'id,profile_id,organization_id,payout_account_id,amount_cents,currency,provider,provider_payout_id,status,status_reason,initiated_at,completed_at,failed_at'

function mapAccount(row: PayoutAccountRow): PayoutAccount {
  return {
    id: row.id,
    bankName: row.bank_name,
    accountNumberMasked: row.account_number_masked,
    accountHolderName: row.account_holder_name,
    isDefault: row.is_default,
    isVerified: row.is_verified,
  }
}

function isPayoutAccount(value: unknown): value is PayoutAccount {
  if (!value || typeof value !== 'object') return false
  const account = value as Record<string, unknown>
  return (
    typeof account.id === 'string' &&
    (typeof account.bankName === 'string' || account.bankName === null) &&
    (typeof account.accountNumberMasked === 'string' || account.accountNumberMasked === null) &&
    (typeof account.accountHolderName === 'string' || account.accountHolderName === null) &&
    typeof account.isDefault === 'boolean' &&
    typeof account.isVerified === 'boolean'
  )
}

function readCreatedAccount(
  result: unknown,
  errorCode: 'PAYOUT_CREATE_FAILED' | 'ORG_PAYOUT_CREATE_FAILED',
) {
  const account = (result as { account?: unknown } | null)?.account
  if (isPayoutAccount(account)) return account

  throw new ServiceError({
    message: 'Payout account edge function returned no display-safe account',
    code: errorCode,
    statusCode: 500,
  })
}

function mapPayoutRow(row: PayoutRow): Payout {
  return {
    id: row.id,
    profileId: row.profile_id,
    organizationId: row.organization_id,
    payoutAccountId: row.payout_account_id,
    amountCents: row.amount_cents,
    currency: row.currency,
    provider: row.provider,
    providerPayoutId: row.provider_payout_id,
    status: row.status,
    statusReason: row.status_reason,
    initiatedAt: row.initiated_at,
    completedAt: row.completed_at,
    failedAt: row.failed_at,
  }
}

class PayoutServiceImpl {
  /**
   * List all payout accounts for the current user.
   */
  async listAccounts(): Promise<PayoutAccount[]> {
    const { data, error } = await sb.rpc('list_my_payout_accounts')

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapAccount)
  }

  /**
   * Submit new bank details via the payout-account edge function.
   * The edge persists the full number for staff to send money, and returns
   * only the masked copy to this client.
   */
  async createPayoutAccount(userId: string, input: PayoutAccountInput): Promise<PayoutAccount> {
    const { data: result, error: fnError } = await supabase.functions.invoke(
      'create-payout-account',
      {
        body: {
          userId,
          accountType: input.accountType,
          bankName: input.bankName,
          bankCode: input.bankCode,
          accountNumber: input.accountNumber,
          accountHolderName: input.accountHolderName,
          documentNumber: input.documentNumber,
          countryCode: input.countryCode,
        },
      },
    )

    if (fnError) {
      throw new ServiceError({
        message: `Payout account creation failed: ${fnError.message}`,
        code: 'PAYOUT_CREATE_FAILED',
        statusCode: 500,
        cause: fnError,
      })
    }

    // The edge function itself returns the canonical, display-safe row. This
    // avoids reopening predicate columns through PostgREST just to re-fetch it.
    return readCreatedAccount(result, 'PAYOUT_CREATE_FAILED')
  }

  /** Switch the personal default atomically; scope comes from auth.uid() in SQL. */
  async setDefaultAccount(accountId: string): Promise<void> {
    const { error } = await sb.rpc('set_default_payout_account', {
      p_account_id: accountId,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * List payout history for the current user (most recent first).
   */
  async listPayouts(userId: string): Promise<Payout[]> {
    const { data, error } = await sb
      .from('payouts')
      .select(PAYOUT_SELECT)
      .eq('profile_id', userId)
      .is('organization_id', null)
      .order('initiated_at', { ascending: false })
      .limit(50)

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapPayoutRow)
  }

  // ─── Organization-scoped payout methods ──────────────────────────────────
  // These are gated by server-side RPC/edge guards: only a fleet_manager of
  // the org can read or mutate its payout account state.
  // The legal representative (sole fleet_manager) configures the corporate
  // bank account; all payouts on org-owned rentals route here.

  async listAccountsForOrg(orgId: string): Promise<PayoutAccount[]> {
    const { data, error } = await sb.rpc('list_organization_payout_accounts', {
      p_organization_id: orgId,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapAccount)
  }

  async createPayoutAccountForOrg(
    orgId: string,
    input: PayoutAccountInput,
  ): Promise<PayoutAccount> {
    const { data: result, error: fnError } = await supabase.functions.invoke(
      'create-payout-account',
      {
        body: {
          organizationId: orgId,
          accountType: input.accountType,
          bankName: input.bankName,
          bankCode: input.bankCode,
          accountNumber: input.accountNumber,
          accountHolderName: input.accountHolderName,
          documentNumber: input.documentNumber,
          countryCode: input.countryCode,
        },
      },
    )

    if (fnError) {
      throw new ServiceError({
        message: `Org payout account creation failed: ${fnError.message}`,
        code: 'ORG_PAYOUT_CREATE_FAILED',
        statusCode: 500,
        cause: fnError,
      })
    }

    return readCreatedAccount(result, 'ORG_PAYOUT_CREATE_FAILED')
  }

  async setDefaultAccountForOrg(orgId: string, accountId: string): Promise<void> {
    const { error } = await sb.rpc('set_default_payout_account', {
      p_account_id: accountId,
      p_organization_id: orgId,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  async listPayoutsForOrg(orgId: string): Promise<Payout[]> {
    const { data, error } = await sb
      .from('payouts')
      .select(PAYOUT_SELECT)
      .eq('organization_id', orgId)
      .order('initiated_at', { ascending: false })
      .limit(50)

    if (error) throw ServiceError.fromSupabaseError(error)
    return (data ?? []).map(mapPayoutRow)
  }

  async getOrgEarningsSummary(orgId: string): Promise<{
    capturedTotalCents: number
    capturedCount: number
    mtdCents: number
    ytdCents: number
    pendingCents: number
  }> {
    const { data, error } = await sb.rpc('fn_org_earnings_summary', { p_org_id: orgId })
    if (error) throw ServiceError.fromSupabaseError(error)
    const row = Array.isArray(data) ? data[0] : data
    return {
      capturedTotalCents: Number(row?.captured_total_cents ?? 0),
      capturedCount: Number(row?.captured_count ?? 0),
      mtdCents: Number(row?.mtd_cents ?? 0),
      ytdCents: Number(row?.ytd_cents ?? 0),
      pendingCents: Number(row?.pending_cents ?? 0),
    }
  }
}

export const payoutService = new PayoutServiceImpl()
